import React, { FC } from 'react';
import { Observer } from 'mobx-react';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { Button, SECONDARY_COLOR_BUTTON_TYPE } from '@shared/components/Button';
import { InputLabel } from '@shared/components/forms/inputs/InputLabel';
import { ToastMessage } from '@shared/components/Toast';
import { ThemeProps } from '@styles/theme';
import { useIntegrationsUIStore } from '@core/useStores';

import { messages } from 'Integrations/integrations.messages';
import { useStyles } from '../../IntegrationsModal.styles';

type LtiTabProps = {
    onRequestClose: () => void;
};

const STEPS = [
    messages['integrations.lti.steps.1'],
    messages['integrations.lti.steps.2'],
    messages['integrations.lti.steps.3'],
];

/** Shows the LTI 1.3 Advantage dynamic registration URL to paste in an LMS. */
export const LtiTab: FC<LtiTabProps> = ({ onRequestClose }) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const integrationsUIStore = useIntegrationsUIStore();

    const copyRegistrationUrl = async () => {
        try {
            await navigator.clipboard.writeText(integrationsUIStore.ltiRegistrationUrl);
            toast.success(
                <ToastMessage
                    message={messages['integrations.lti.copy.success']}
                    type='success'
                />
            );
        } catch {
            toast.error(
                <ToastMessage
                    message={messages['integrations.lti.copy.error']}
                    type='error'
                />
            );
        }
    };

    return (
        <Observer>
            {() => (
                <>
                    <Typography variant='body2' color='textSecondary' className={styles.tabDescription}>
                        {messages['integrations.lti.description']}
                    </Typography>

                    <div className={styles.fieldGroup}>
                        <InputLabel label={messages['integrations.lti.url.label']} />
                        {integrationsUIStore.ltiRegistrationUrl ? (
                            <TextField
                                size='small'
                                className={styles.field}
                                value={integrationsUIStore.ltiRegistrationUrl}
                                // Selecting the whole URL on focus makes a manual copy
                                // one keystroke away if the clipboard is unavailable.
                                onFocus={event => event.target.select()}
                                helperText={integrationsUIStore.companyName
                                    ? `${messages['integrations.lti.url.company']} ${integrationsUIStore.companyName}.`
                                    : undefined}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            <Tooltip title={messages['integrations.lti.copy']}>
                                                <IconButton
                                                    onClick={copyRegistrationUrl}
                                                    aria-label={messages['integrations.lti.copy']}
                                                    edge='end'
                                                >
                                                    <ContentCopyIcon fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        ) : (
                            <Typography variant='body2' color='textSecondary'>
                                {messages['integrations.lti.url.unavailable']}
                            </Typography>
                        )}
                    </div>

                    <div className={styles.steps}>
                        <span className={styles.stepsTitle}>
                            {messages['integrations.lti.steps.title']}
                        </span>
                        <ol className={styles.stepsList}>
                            {STEPS.map(step => <li key={step}>{step}</li>)}
                        </ol>
                    </div>

                    <div className={styles.footer}>
                        <Button color={SECONDARY_COLOR_BUTTON_TYPE} onClick={onRequestClose}>
                            {messages['integrations.close.button']}
                        </Button>
                    </div>
                </>
            )}
        </Observer>
    );
};
